import React, { FC, useEffect } from 'react';

import ScrollToTop from '@components/shared/ScrollToTop/ScrollToTop';

import { Card, Descriptions, Spin, Typography } from 'antd';

import { useLazyGetPlanTermsQuery } from '@store/coverage';
import { useParams } from 'react-router-dom';

const { Title, Text } = Typography;

interface Term {
  risk: string;
  coverage_amount: number;
  property: string;
}

const PlanTerms: FC = () => {
  const { planNumber } = useParams<{ planNumber: string }>();
  const [triggerGetPlanTerms, { data, isLoading }] = useLazyGetPlanTermsQuery();

  useEffect(() => {
    if (planNumber) {
      triggerGetPlanTerms(planNumber);
    }
  }, [planNumber, triggerGetPlanTerms]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="p-6">
      <ScrollToTop />
      <Title
        level={1}
        className="text-center my-5 py-5 font-bold text-2xl bg-[#59DFE533]"
      >
        Plan Terms - Plan #{planNumber}
      </Title>
      {isLoading ? (
        <Spin size="large" className="flex justify-center" />
      ) : data ? (
        <div className="space-y-6">
          <Card title="Plan Information" className="shadow">
            <Descriptions bordered>
              <Descriptions.Item label="Plan Number" span={3}>
                {data.plan_number}
              </Descriptions.Item>
              <Descriptions.Item label="Version" span={3}>
                {data.version}
              </Descriptions.Item>
              <Descriptions.Item label="Effective Date" span={3}>
                {formatDate(data.effective_date)}
              </Descriptions.Item>
              <Descriptions.Item label="Affiliate" span={3}>
                {data.metadata.affiliate}
              </Descriptions.Item>
              <Descriptions.Item label="Created By" span={3}>
                {data.metadata.created_by}
              </Descriptions.Item>
              <Descriptions.Item label="Created At" span={3}>
                {formatDate(data.metadata.created_at)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Coverage Details" className="shadow">
            {data.terms.map((term: Term, index: number) => (
              <div key={index} className="mb-4 last:mb-0">
                <Descriptions bordered>
                  <Descriptions.Item label="Risk Type" span={3}>
                    {term.risk}
                  </Descriptions.Item>
                  <Descriptions.Item label="Coverage Amount" span={3}>
                    {formatCurrency(term.coverage_amount)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Property" span={3}>
                    {term.property}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            ))}
          </Card>
        </div>
      ) : (
        <Text>No terms available for this plan.</Text>
      )}
    </div>
  );
};

export default PlanTerms;
